import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { CompanyDetail as CompanyShape, OnboardingStatus, SetupStep, SetupStepKey } from '@dru/types';
import { Alert, Button, Card, StatusBadge, cn } from '@dru/ui';
import { Check, Circle, MinusCircle } from 'lucide-react';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';

/**
 * Where a company is between "somebody registered it" and "its HR is working
 * in it", and the one thing to do next.
 *
 * Four stages — Registered, Approved, Set up, Live — read from the company's
 * status and the setup figures the API computes from what exists. The steps
 * are the server's own list (`/admin/tenants/:id/setup`); nothing here decides
 * a step is done. The single next action goes to the place that does the
 * work: the approval dialog, a tab of this page, the import screen, or the
 * existing "Finish setup" call. Hidden once setup is finished, and for a
 * closed or suspended account, where the header's attention list speaks.
 */
export function OnboardingGuide({ company: c, setup, setupLoading, can, onApprove, onEdit, setTab, onChanged }: {
  company: CompanyShape;
  setup: OnboardingStatus | null;
  setupLoading: boolean;
  can: { lifecycle: boolean; setup: boolean; fullSetup: boolean; edit: boolean };
  onApprove: () => void;
  onEdit: () => void;
  setTab: (key: string) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = c.status === 'PENDING';
  const active = c.status === 'ACTIVE';
  if (!pending && !active) return null;
  if (active && setup?.setupStatus === 'COMPLETED') return null;
  if (active && !setup) return setupLoading && can.setup ? <Card className="h-28 animate-pulse" ><span className="sr-only">Loading setup</span></Card> : null;

  async function finish() {
    setBusy(true); setError(null);
    try { await api.post(`/admin/tenants/${c.id}/setup/status`, { status: 'COMPLETED' }); onChanged(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  const stages: Array<{ key: string; label: string; state: 'done' | 'current' | 'todo'; note?: string }> = [
    { key: 'registered', label: 'Registered', state: 'done', note: pending ? 'Self-registered online' : undefined },
    { key: 'approved', label: 'Approved', state: pending ? 'current' : 'done' },
    {
      key: 'setup', label: 'Set up',
      state: pending ? 'todo' : 'current',
      note: setup ? `${setup.progress.completed} of ${setup.progress.total} steps` : undefined,
    },
    { key: 'live', label: 'Live', state: 'todo', note: 'HR working in it' },
  ];

  // ── The one next action ─────────────────────────────────────────
  let next: { title: string; why: string; action: ReactNode } | null = null;
  if (pending) {
    const missing = [
      !c.contactEmail && 'a contact email (it becomes the first administrator)',
      !c.gstin && 'a GSTIN',
      !(c.city || c.pincode) && 'an address',
    ].filter(Boolean) as string[];
    next = {
      title: 'Review and approve this registration',
      why: missing.length
        ? `It arrived without ${missing.join(', ')}. Check the details below, add what is missing, then approve. Approving activates the company and invites its first administrator.`
        : 'The company registered itself. Check the details below, then approve: that activates it and invites its first administrator. Nobody can sign in until then.',
      action: can.lifecycle
        ? <Button onClick={onApprove}>Review and approve</Button>
        : <span className="text-small text-neutral-600">An administrator with approval rights approves it.</span>,
    };
  } else if (setup) {
    const step = setup.steps.find((s) => s.key === setup.current) ?? setup.steps.find((s) => !s.done && !s.skipped);
    if (step) next = nextFor(step, setup, { c, can, onEdit, setTab, finish, busy });
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4">
        <div className="min-w-0">
          <h2 className="text-h4 text-neutral-900">{pending ? 'New registration' : 'Getting this company ready'}</h2>
          <p className="text-small text-neutral-600">
            {pending
              ? 'Registered from the public sign-up form, not by our team. It waits here until somebody approves or rejects it.'
              : 'Approved and active. These steps get its HR working; the medical packages are managed by our team.'}
          </p>
        </div>
      </div>

      {/* Stage rail */}
      <ol aria-label="Onboarding stages" className="mx-5 mt-4 grid grid-cols-4 gap-1">
        {stages.map((s, i) => (
          <li key={s.key} aria-current={s.state === 'current' ? 'step' : undefined} className="min-w-0">
            <div className={cn('h-1.5 rounded-full',
              s.state === 'done' ? 'bg-covered-600' : s.state === 'current' ? 'bg-primary-600' : 'bg-neutral-200')} />
            <div className={cn('mt-1.5 truncate text-caption font-semibold',
              s.state === 'todo' ? 'text-neutral-500' : 'text-neutral-900')}>
              <span className="sr-only">Stage {i + 1}: </span>{s.label}
            </div>
            {s.note && <div className="truncate text-caption text-neutral-500 max-sm:hidden">{s.note}</div>}
          </li>
        ))}
      </ol>

      {next && (
        <div className="mx-5 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-card border border-primary-200 bg-primary-50 px-4 py-3">
          <div className="min-w-0 flex-[1_1_20rem]">
            <div className="text-caption font-semibold uppercase tracking-wider text-primary-700">Next step</div>
            <div className="text-body font-semibold text-neutral-900">{next.title}</div>
            <p className="text-small text-neutral-700">{next.why}</p>
          </div>
          <div className="shrink-0">{next.action}</div>
        </div>
      )}
      {error && <div className="mx-5 mt-3"><Alert tone="danger">{error}</Alert></div>}

      {setup && !pending && (
        <details className="group mx-5 mb-4 mt-3">
          <summary className="cursor-pointer text-small font-medium text-primary-600 hover:text-primary-700 max-md:min-h-10 max-md:flex max-md:items-center">
            All setup steps ({setup.progress.completed} of {setup.progress.total} done)
          </summary>
          <ol className="mt-2 divide-y divide-neutral-100 rounded-card border border-neutral-200">
            {setup.steps.map((s) => <StepRow key={s.key} step={s} current={s.key === setup.current} />)}
          </ol>
        </details>
      )}
      {(pending || !setup) && <div className="h-4" />}
    </Card>
  );
}

function StepRow({ step: s, current }: { step: SetupStep; current: boolean }) {
  const done = s.actuallyDone ?? s.done;
  return (
    <li className={cn('flex items-start gap-3 px-3 py-2.5', current && 'bg-primary-50/60')}>
      <span className="mt-0.5 shrink-0" aria-hidden>
        {done ? <Check className="h-4 w-4 text-covered-600" />
          : s.skipped ? <MinusCircle className="h-4 w-4 text-neutral-400" />
            : <Circle className={cn('h-4 w-4', current ? 'text-primary-600' : 'text-neutral-300')} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-small font-medium text-neutral-900">{s.title}</span>
          <span className="sr-only">{done ? 'done' : s.skipped ? 'skipped' : s.required ? 'required' : 'optional'}</span>
          {!done && !s.skipped && (
            <StatusBadge tone={s.required ? 'pending' : 'neutral'}>{s.required ? 'Required' : 'Optional'}</StatusBadge>
          )}
        </div>
        {s.detail && <div className="text-caption text-neutral-600">{s.detail}</div>}
        {!done && s.missing?.length ? <div className="text-caption text-warning-600">Missing: {s.missing.join(', ')}</div> : null}
      </div>
    </li>
  );
}

const STEP_TAB: Partial<Record<SetupStepKey, string>> = { plants: 'plants', users: 'users', medical: 'medical' };

function nextFor(step: SetupStep, setup: OnboardingStatus, x: {
  c: CompanyShape;
  can: { setup: boolean; fullSetup: boolean; edit: boolean };
  onEdit: () => void; setTab: (k: string) => void; finish: () => void; busy: boolean;
}): { title: string; why: string; action: ReactNode } {
  const noRights = <span className="text-small text-neutral-600">Needs setup rights.</span>;
  switch (step.key) {
    case 'profile':
    case 'location':
      return {
        title: step.key === 'profile' ? 'Complete the company profile' : 'Add the company address',
        why: step.missing?.length ? `Missing: ${step.missing.join(', ')}.` : step.hint || step.detail,
        action: x.can.edit ? <Button onClick={x.onEdit}>Edit details</Button> : noRights,
      };
    case 'employees':
      return {
        title: 'Add the employees',
        why: setup.suggestion?.text ?? 'Import a list, or let the company\'s HR add them once they sign in.',
        action: x.can.setup ? (
          <Link to={`/admin/companies/${x.c.id}/employees/import`}
            className="inline-flex h-10 items-center rounded-btn bg-primary-600 px-4 text-body font-semibold text-white hover:bg-primary-700">
            Import employees
          </Link>
        ) : noRights,
      };
    case 'review': {
      const blocked = setup.missingRequired.length > 0;
      return {
        title: blocked ? 'Finish the required steps' : 'Finish setup',
        why: blocked
          ? `Still required: ${setup.missingRequired.join(', ')}.`
          : 'Every required step is done. Finishing marks the company live; it can be reopened later.',
        action: x.can.fullSetup
          ? <Button onClick={x.finish} loading={x.busy} disabled={blocked}>Finish setup</Button>
          : noRights,
      };
    }
    default: {
      const tab = STEP_TAB[step.key];
      const label = step.key === 'users' ? 'Invite HR' : step.key === 'plants' ? 'Manage plants' : 'Open packages';
      return {
        title: step.key === 'users' ? 'Invite the company\'s HR' : step.title,
        why: step.missing?.length ? `Missing: ${step.missing.join(', ')}.` : step.hint || step.detail,
        action: tab && (step.key === 'plants' ? x.can.setup : x.can.fullSetup)
          ? <Button onClick={() => x.setTab(tab)}>{label}</Button>
          : noRights,
      };
    }
  }
}
